import '@justgiving/loading/src/Loading.scss';

import React, { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { NetworkStatus, gql } from '@apollo/client';
import { sha256 } from 'js-sha256';

import { SupportersListQuery } from '../../../types/__generated__';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';
import { SIZE_136_136 } from '../../constants/imageTemplateStrings';
import useFullPageQuery from '../../hooks/useFullPageQuery';
import { makePhotoUrl } from '../../utils/makePhotoUrl';
import DonateButton from '../DonateButton/DonateButton';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import PageSection from '../PageSection/PageSection';
import SupporterDetails from '../SupporterDetails/SupporterDetails';
import SupporterDetailsPlaceholder from '../SupporterDetails/SupporterDetailsPlaceholder';
import SupportersListSkeleton from './SupportersListSkeleton';
import PAGE_TYPES from '../../constants/pageTypes';

import styles from './SupportersList.scss';

export const DOCUMENT = gql`
  query SupportersList(
    $after: String
    $type: PageType!
    $slug: Slug
    $preview: Preview
    $id: ID
  ) {
    page(type: $type, slug: $slug, id: $id, preview: $preview) {
      id
      legacyId
      donationSummary {
        onlineDonationCount
        donationCount
      }
      donations(first: 5, after: $after) {
        edges {
          node {
            id
            legacyId
            creationDate
            amount {
              value
              currencyCode
            }
            giftAidAmount {
              value
              currencyCode
            }
            displayName
            avatar
            message
          }
        }
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

type SupportersListProps = {
  noSupportersMessage: string;
  displayDonateButton?: boolean;
  donateButtonExperiments?: string[];
};

const SupportersList: FunctionComponent<SupportersListProps> = ({
  noSupportersMessage,
  displayDonateButton,
  donateButtonExperiments,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageType } = useNotNullableContext(PageMetaContext);
  const { error, data, fetchMore, refetch, networkStatus } = useFullPageQuery<
    SupportersListQuery
  >(DOCUMENT, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  if (
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.refetch
  ) {
    return <SupportersListSkeleton animated displayRefreshButton={false} />;
  }

  if (error) {
    return <SupportersListSkeleton onRefresh={refetch} />;
  }

  if (!data?.page?.donations) {
    return null;
  }

  const {
    page: {
      legacyId,
      donationSummary,
      donations: {
        edges,
        pageInfo: { hasNextPage, endCursor },
      },
    },
  } = data;

  async function handleFetchMore() {
    await fetchMore({
      variables: { after: endCursor },
    });
  }

  // TODO: Hack to suppress all donor generated content for a page when necessary.
  // This approach obfuscates the slugs of the affected pages in source code.
  // Comments are stripped from compiled code, so commenting the slug is safe - and useful!
  const suppressDonorGeneratedContent = [
    'd048b26b1960d805c8317584ee704e466014cda0055d6b6832286362ac7c1d48', // Matt-Hancock-London-Marathon
    'd3cfe8cc2c96f00681107a27f9accfa58609a902f31997a658041f50d73a3f65', // Matt-Hancock-Climbs-Mont-Blanc
    '312749ef6913f8ccba9a3881cee79a8321c95852688c8496e955811445f7668e', // Gina-Coladangelo1
  ].includes(sha256.hex(legacyId));

  const donationCount =
    pageType === PAGE_TYPES.TEAM
      ? donationSummary?.onlineDonationCount
      : donationSummary?.donationCount;

  return (
    <PageSection
      title={
        donationCount === 0
          ? t('supporters.noSupporters', 'No supporters yet')
          : t('supporters.title', 'Supporters')
      }
      count={donationCount ?? 0}
      className={styles.container}
    >
      <ul data-qa="supporters-list">
        {(donationCount ?? 0) > 0 &&
          edges.map(supporter => {
            const {
              avatar,
              amount,
              giftAidAmount,
              message,
              creationDate,
              displayName,
              legacyId,
            } = supporter.node;
            return (
              <SupporterDetails
                key={legacyId}
                image={
                  suppressDonorGeneratedContent
                    ? undefined
                    : makePhotoUrl(avatar, SIZE_136_136)
                }
                message={suppressDonorGeneratedContent ? null : message}
                currencyCode={
                  amount && amount.currencyCode ? amount.currencyCode : 'GBP'
                }
                donationAmount={amount && amount.value ? amount.value / 100 : 0}
                giftAidAmount={
                  giftAidAmount && giftAidAmount.value
                    ? giftAidAmount.value / 100
                    : 0
                }
                date={creationDate}
                userName={suppressDonorGeneratedContent ? null : displayName}
              />
            );
          })}

        {(donationCount ?? 0) === 0 && (
          <>
            <p className="jg-space-mbn" data-qa="no-supporters">
              {noSupportersMessage}
            </p>
            <SupporterDetailsPlaceholder />
          </>
        )}
      </ul>

      {hasNextPage && (
        <LoadMoreButton
          className={styles.button}
          loading={networkStatus === NetworkStatus.fetchMore}
          onClick={handleFetchMore}
          trackingData={{
            action_name: 'click',
            subtype: 'button',
            event: 'click',
            event_value: 'show more',
            sub_section: 'supporters',
          }}
        >
          <Trans i18nKey="supporters.showMore">
            Show more
            <span className="jg-sr-only"> supporters</span>
          </Trans>
        </LoadMoreButton>
      )}

      {displayDonateButton && (
        <DonateButton
          className={styles.button}
          inactivePageBehaviour="hide"
          trackingDataPageSection="SupportersList"
          experiments={donateButtonExperiments}
          successType="SupportersDonateButtonClick"
        />
      )}
    </PageSection>
  );
};

export default SupportersList;
